import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowRight, CheckCircle2, XCircle, FileText, Calendar, Users, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

export default function SuiviChantierPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO - Message différenciateur */}
      <section className="py-16 sm:py-20 lg:py-24" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge 
              className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase tracking-wide"
              style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
            >
              Notre différence
            </Badge>
            
            <h1 
              className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold leading-[1.05]"
              style={{ color: 'var(--hr-text)' }}
              data-testid="suivi-title"
            >
              On ne vous laisse pas seul au milieu des travaux
            </h1>
            
            <p 
              className="text-xl leading-relaxed"
              style={{ color: 'var(--hr-text-muted)' }}
            >
              Le vrai problème des rénovations ? Personne ne coordonne. Nous, si.
            </p>

            <div className="pt-4">
              <Button
                size="lg"
                className="rounded-xl px-8"
                style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                onClick={() => navigate('/devis')}
                data-testid="suivi-hero-cta"
              >
                Découvrir notre accompagnement
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CE QUE VOUS NE VOULEZ PLUS GÉRER */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
                Ce que vous ne voulez plus gérer
              </h2>
              <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>
                Sans pilotage, c'est vous qui faites tout ça :
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Appeler 10 artisans pour avoir 3 devis',
                'Coordonner peintre, électricien, plombier',
                'Gérer les retards et les absences',
                'Vérifier si le travail est bien fait',
                'Anticiper les conflits entre corps de métier',
                'Adapter le planning quand ça dérape',
                'Recevoir le chantier et constater les défauts',
                'Relancer pour les finitions oubliées'
              ].map((stress, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
                  <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-danger)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text)' }}>{stress}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 rounded-2xl border" style={{ borderColor: 'var(--hr-border)', backgroundColor: 'var(--hr-bg)' }}>
              <p className="text-lg font-medium text-center" style={{ color: 'var(--hr-text)' }}>
                Résultat : vous perdez du temps, de l'argent, et vos nerfs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CE QUE NOUS PILOTONS */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--hr-accent)' }}>
              Notre rôle
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Ce que nous pilotons pour vous
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--hr-text-muted)' }}>
              Vous avez un seul interlocuteur. Nous coordonnons tout le reste.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: 'Coordination des artisans',
                items: [
                  'Sélection des intervenants',
                  'Planning des interventions',
                  'Suivi de l\'avancement',
                  'Gestion des priorités'
                ]
              },
              {
                icon: Shield,
                title: 'Contrôle qualité',
                items: [
                  'Vérifications hebdomadaires',
                  'Points de contrôle',
                  'Correction des défauts',
                  'Réception finale'
                ]
              },
              {
                icon: Calendar,
                title: 'Respect des délais',
                items: [
                  'Planning détaillé',
                  'Suivi des jalons',
                  'Anticipation des blocages',
                  'Communication transparente'
                ]
              }
            ].map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
                      <pillar.icon className="h-6 w-6" style={{ color: 'var(--hr-primary)' }} />
                    </div>
                    <CardTitle className="font-['Figtree'] text-xl font-semibold" style={{ color: 'var(--hr-text)' }}>
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {pillar.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-success)' }} />
                          <span className="text-sm" style={{ color: 'var(--hr-text)' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TABLE RESPONSABILITÉS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Qui fait quoi, concrètement
            </h2>
          </div>

          <Card className="rounded-2xl border-[color:var(--hr-border)]" data-testid="responsibility-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderColor: 'var(--hr-border)' }}>
                      <TableHead className="font-['Figtree'] font-semibold" style={{ color: 'var(--hr-text)' }}>Tâche</TableHead>
                      <TableHead className="font-['Figtree'] font-semibold text-center" style={{ color: 'var(--hr-text)' }}>Sans Homely</TableHead>
                      <TableHead className="font-['Figtree'] font-semibold text-center" style={{ color: 'var(--hr-text)' }}>Avec Homely</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { task: 'Coordination artisans', without: 'Vous', with: 'Homely' },
                      { task: 'Suivi planning', without: 'Vous', with: 'Homely' },
                      { task: 'Contrôle qualité', without: 'Vous', with: 'Homely' },
                      { task: 'Gestion imprévus', without: 'Vous', with: 'Homely' },
                      { task: 'Point hebdomadaire', without: '—', with: 'Homely' },
                      { task: 'Réception chantier', without: 'Vous seul', with: 'Homely + Vous' },
                      { task: 'Validation finale', without: 'Vous', with: 'Vous (serein)' }
                    ].map((row, i) => (
                      <TableRow key={i} style={{ borderColor: 'var(--hr-border)' }}>
                        <TableCell className="font-medium" style={{ color: 'var(--hr-text)' }}>{row.task}</TableCell>
                        <TableCell className="text-center" style={{ color: 'var(--hr-text-muted)' }}>{row.without}</TableCell>
                        <TableCell className="text-center font-medium" style={{ color: 'var(--hr-success)' }}>{row.with}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CADENCE DE SUIVI */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
                Cadence de suivi
              </h2>
              <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>
                Vous savez toujours où on en est.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { freq: 'Hebdomadaire', action: 'Point d\'avancement complet par email ou visio', color: 'var(--hr-accent)' },
                { freq: 'Visites terrain', action: 'Contrôles qualité réguliers sur place', color: 'var(--hr-primary)' },
                { freq: 'Temps réel', action: 'Disponibilité pour vos questions et ajustements', color: 'var(--hr-success)' }
              ].map((item, i) => (
                <Card key={i} className="rounded-2xl border-[color:var(--hr-border)]">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color }}>
                      <span className="text-white font-bold">{i + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Figtree'] font-semibold mb-1" style={{ color: 'var(--hr-text)' }}>{item.freq}</h3>
                      <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>{item.action}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EXEMPLE COMPTE-RENDU */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-4 mb-8">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Exemple de compte-rendu hebdomadaire
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>
              Voici à quoi ressemble notre suivi.
            </p>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="lg" 
                className="rounded-xl"
                data-testid="report-example-open"
              >
                <FileText className="mr-2 h-5 w-5" />
                Voir un exemple
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-2xl" style={{ color: 'var(--hr-text)' }}>
                  Compte-rendu hebdo - Semaine 3
                </DialogTitle>
                <DialogDescription>
                  Rénovation salle de bain, Genève
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-left">
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: 'var(--hr-text)' }}>✅ Réalisé cette semaine</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm" style={{ color: 'var(--hr-text-muted)' }}>
                    <li>Pose carrelage mural terminée</li>
                    <li>Installation douche italienne OK</li>
                    <li>Plomberie vérifiée, aucune fuite</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: 'var(--hr-text)' }}>📅 Prévu semaine prochaine</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm" style={{ color: 'var(--hr-text-muted)' }}>
                    <li>Pose meuble vasque (lundi)</li>
                    <li>Finitions électricité (mardi-mercredi)</li>
                    <li>Peinture plafond (jeudi)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: 'var(--hr-text)' }}>⚠️ Points d'attention</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm" style={{ color: 'var(--hr-text-muted)' }}>
                    <li>Délai livraison miroir repoussé de 2 jours → planning ajusté</li>
                  </ul>
                </div>
                <div className="pt-4 border-t" style={{ borderColor: 'var(--hr-border)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                    📊 Avancement global : 65% • Livraison prévue : dans 3 semaines
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 sm:py-24" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 
            className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold"
            style={{ color: 'var(--hr-primary-fg)' }}
          >
            Prêt à déléguer la coordination ?
          </h2>
          <p 
            className="text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: 'rgba(245,243,239,0.9)' }}
          >
            Demandez un devis. Nous vous expliquons comment nous pilotons votre projet de A à Z.
          </p>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
            data-testid="suivi-final-cta"
          >
            Recevoir mon devis cadré
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}
